import React, { useEffect, useState } from 'react';
import { PageHeader, StatCard, Card, Spinner, Badge, Btn, useToast } from '../../components/shared/UI';
import api from '../../utils/api';

const fmtBytes = b => {
  if (b == null) return '—';
  const u = ['B','KB','MB','GB','TB'];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 ? 2 : 1)} ${u[i]}`;
};
const fmtUptime = s => {
  const d = Math.floor(s / 86400); s %= 86400;
  const h = Math.floor(s / 3600);  s %= 3600;
  const m = Math.floor(s / 60);
  return [d && `${d}d`, h && `${h}h`, `${m}m`].filter(Boolean).join(' ');
};

export default function SystemStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);
  const { show, ToastContainer } = useToast();

  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/admin/health-report');
      show(`Report sent to ${data.to}`);
    } catch (e) {
      show(e.response?.data?.message || 'Send failed', 'error');
    } finally { setSending(false); }
  };

  const load = () => {
    setLoading(true);
    api.get('/admin/stats')
      .then(r => setStats(r.data))
      .catch(e => setErr(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

  if (loading && !stats) return <Spinner />;
  if (err) return <Card><p style={{ color: 'var(--danger)' }}>{err}</p></Card>;

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title="System Performance"
        subtitle="Live database, server, and usage metrics (refresh 15s)"
        actions={<Btn variant="outline" onClick={sendReport} disabled={sending}>{sending ? 'Sending…' : '✉ Send Health Report Now'}</Btn>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Users"     value={stats.counts.users}     icon="👥" color="#2E75B6" />
        <StatCard label="Documents" value={stats.counts.documents} icon="📄" color="#10B981" />
        <StatCard label="Clients"   value={stats.counts.clients}   icon="🤝" color="#F59E0B" />
        <StatCard label="DB Size"   value={fmtBytes(stats.database.sizeBytes)} icon="💾" color="#8B5CF6" />
        <StatCard label="Uptime"    value={fmtUptime(stats.server.uptimeSec)}  icon="⏱"  color="#06B6D4" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 16 }}>
        {/* Database */}
        <Card>
          <h3 style={cardH}>Database</h3>
          <Row k="Data size"     v={fmtBytes(stats.database.sizeBytes)} />
          <Row k="Storage size"  v={fmtBytes(stats.database.storageBytes)} />
          <Row k="Indexes"       v={fmtBytes(stats.database.indexesBytes)} />
          <Row k="Collections"   v={stats.database.collections} />
        </Card>

        {/* Server */}
        <Card>
          <h3 style={cardH}>Server</h3>
          <Row k="Node"          v={stats.server.nodeVersion} />
          <Row k="Platform"      v={stats.server.platform} />
          <Row k="Heap used"     v={`${stats.server.memoryHeapUsedMB} MB`} />
          <Row k="RSS"           v={`${stats.server.memoryRssMB} MB`} />
          <Row k="System mem"    v={`${stats.server.freeMemMB} / ${stats.server.totalMemMB} MB free`} />
          <Row k="Load avg (1m)" v={stats.server.loadAvg[0].toFixed(2)} />
        </Card>

        {/* Recent users */}
        <Card>
          <h3 style={cardH}>Recent Users</h3>
          {stats.recentUsers.length === 0
            ? <p style={muted}>No users yet</p>
            : stats.recentUsers.map(u => (
                <div key={u._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{u.email}</div>
                  </div>
                  <Badge label={u.role} color={u.role === 'admin' ? '#F59E0B' : '#64748b'} />
                </div>
              ))
          }
        </Card>

        {/* Plans */}
        <Card>
          <h3 style={cardH}>Subscription Distribution</h3>
          {stats.usersByPlan.length === 0
            ? <p style={muted}>No data</p>
            : stats.usersByPlan.map(p => (
                <Row key={p._id || 'none'} k={p._id || 'none'} v={p.count} />
              ))
          }
        </Card>

        {/* Doc types */}
        <Card>
          <h3 style={cardH}>Documents by Type</h3>
          {stats.docsByType.length === 0
            ? <p style={muted}>No documents</p>
            : stats.docsByType.map(d => <Row key={d._id} k={d._id} v={d.count} />)
          }
        </Card>
      </div>
    </div>
  );
}

const cardH = { fontSize: 14, fontWeight: 600, color: 'var(--heading)', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border-light)' };
const muted = { fontSize: 13, color: 'var(--text-subtle)' };

function Row({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
      <span style={{ color: 'var(--text-muted)' }}>{k}</span>
      <span style={{ fontWeight: 500, color: 'var(--text)' }}>{v}</span>
    </div>
  );
}
